import { ChevronDownIcon, ChevronUpIcon } from "@datagame/ui";
import { useState } from "react";
import classNames from "classnames/bind";

import { type Folder } from "../FoldersList";
import { FolderItem } from "./FolderItem";

import styles from "./FolderGroup.module.scss";
import { useDrop } from "react-dnd";
import { useMutation } from "@apollo/client";
import { DIRECTORY_UPDATE } from "../../../../../../graphql/mutations/FolderMutations";

const cx = classNames.bind(styles);

export type FolderWithChildren = Folder & {
  children?: Folder[];
  virtual?: boolean;
};

export interface FolderProps {
  folder: FolderWithChildren;
  isEditable?: boolean;
  selected?: Folder;
  setSelected?: (folder: Folder) => void;
  className?: string;
}

export const FolderGroup = ({ folder, selected, setSelected, isEditable }: FolderProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [updateDirectory] = useMutation(DIRECTORY_UPDATE);
  const [{ isOver, item }, drop] = useDrop(
    () => ({
      accept: "FOLDER",
      drop: (item) => {
        if (item.id === folder.id) return;

        updateDirectory({
          variables: {
            directoryId: item.id,
            data: {
              parentDirectoryId: folder.id,
            },
          },
        });
      },
      canDrop: (item: FolderWithChildren) => {
        return item.parentDirectory?.id !== folder.id;
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver({ shallow: true }),
        item: monitor.getItem<FolderWithChildren>(),
      }),
    }),
    [folder],
  );

  const isOverCurrent = (isOver && item?.parentDirectory?.id === folder.id) || item?.id === folder.id;

  const childrenList =
    isOver && !isOverCurrent
      ? [
          {
            id: item.id,
            name: item.name,
            parentDirectory: folder.parentDirectory,
            default: item.default,
            virtual: true,
          },
          ...(folder.children || []),
        ]
      : folder.children || [];
  const isExpandedWithDop = isOver && !isOverCurrent ? true : isExpanded;

  const canBeExpanded = Boolean(folder.children?.length);

  const chevronClassName = cx("chevron", {
    selected: selected?.id === folder.id || (selected && folder.children?.map((item) => item.id).includes(selected.id)),
  });

  const renderChevron = () => {
    if (!canBeExpanded) {
      return <div className={styles.chevronPlaceholder}></div>;
    }
    if (isExpanded) {
      return <ChevronUpIcon onClick={() => setIsExpanded(false)} className={chevronClassName} />;
    }
    return <ChevronDownIcon onClick={() => setIsExpanded(true)} className={chevronClassName} />;
  };

  return (
    <>
      <div ref={drop} className={styles.folderWrapper}>
        {renderChevron()}
        <FolderItem folder={folder} selected={selected} setSelected={setSelected} isEditable={isEditable} />
      </div>
      {isExpandedWithDop && Boolean(childrenList.length) && (
        <div className={styles.childrenList}>
          {childrenList.map((item) => {
            return (
              <FolderItem
                key={item.id}
                selected={selected}
                folder={item}
                setSelected={setSelected}
                isEditable={isEditable}
                className={styles.childrenFolder}
              />
            );
          })}
        </div>
      )}
    </>
  );
};
